import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"

import type { OfferHealthSnapshot } from "../../services/use-offer-health"
import { usePrefersReducedMotion } from "../../hooks/use-prefers-reduced-motion"
import { CHART_TOOLTIP_CONTENT_STYLE } from "../ui/chart-style"

type OfferHealthChartProps = {
  /** Snapshots oldest→newest (as the trend endpoint returns them). */
  trend: OfferHealthSnapshot[]
}

/** Stacked column chart of offer coverage over time: active offers (filled) on
 *  inactive (muted) per snapshot, so the bar height is the total and the split
 *  shows the trend — is the active share shrinking? Mirrors SalesChart's recharts
 *  styling (theme tokens, no hardcoded colors). */
export function OfferHealthChart({ trend }: OfferHealthChartProps) {
  const prefersReducedMotion = usePrefersReducedMotion()
  const data = trend.map((snapshot) => ({
    label: formatCapturedAt(snapshot.capturedAt),
    active: snapshot.active,
    inactive: snapshot.inactive,
    unknown: snapshot.unknown ?? null,
  }))

  return (
    <div className="w-full" aria-label="Offer activity: active, inactive, and unknown">
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />

          <XAxis
            dataKey="label"
            tick={{ fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
          />

          <YAxis
            tick={{ fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            width={40}
          />

          <Tooltip
            formatter={(value, name) => [
              Number(value).toLocaleString(),
              String(name).replace(/^./, (letter) => letter.toUpperCase()),
            ]}
            contentStyle={CHART_TOOLTIP_CONTENT_STYLE}
            cursor={{ className: "fill-muted" }}
          />

          <Bar
            dataKey="active"
            stackId="offers"
            fill="var(--color-muted-foreground)"
            isAnimationActive={!prefersReducedMotion}
            animationDuration={460}
            animationEasing="ease-out"
          />

          <Bar
            dataKey="inactive"
            stackId="offers"
            fill="var(--color-warning)"
            opacity={0.5}
            radius={[3, 3, 0, 0]}
            isAnimationActive={!prefersReducedMotion}
            animationDuration={460}
            animationEasing="ease-out"
          />

          <Bar
            dataKey="unknown"
            stackId="offers"
            fill="var(--color-muted)"
            isAnimationActive={!prefersReducedMotion}
          />
        </BarChart>
      </ResponsiveContainer>

      {trend.some((snapshot) => snapshot.unknown == null) && (
        <p className="text-xs text-muted-foreground">Historical unknown counts unavailable.</p>
      )}
    </div>
  )
}

/** `2026-06-22 11:07:42.571+00` → `22.6. 11:07`. Includes the time because the
 *  daily cron can be re-run intra-day, so date alone would collide. Postgres
 *  timestamptz uses a space and a 2-digit offset (`+00`), neither of which JS's
 *  Date parses — normalize to ISO (`T`, `+00:00`) first. */
function formatCapturedAt(capturedAt: string): string {
  const iso = capturedAt.replace(" ", "T").replace(/([+-]\d{2})$/, "$1:00")
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) {
    return capturedAt
  }
  const hh = String(parsed.getHours()).padStart(2, "0")
  const mm = String(parsed.getMinutes()).padStart(2, "0")
  return `${parsed.getDate()}.${parsed.getMonth() + 1}. ${hh}:${mm}`
}
